import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { 
  ShieldAlert, UserPlus, FileText, FileSpreadsheet, FileIcon, 
  Download, Edit, Trash2, Activity, ShieldCheck, Mail, Lock, Power, UserX, UserMinus, Pause
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { generateExport } from "@/utils/exportUtils";
import { 
  getAdmins, saveAdmins, addAdmin as storeAddAdmin, updateAdmin, removeAdmin,
  getActivities, logAdminActivity, AdminUser, AdminActivity 
} from "@/data/adminsStore";

const ALL_CATEGORIES = [
  "Tableau de bord & Analyses",
  "Utilisateurs & Accréditations",
  "Contenu & Apparence",
  "Élections & Partis",
  "Participation Citoyenne",
  "Média & Communication",
  "Paramètres & Système"
];

export default function AdminAdminsPage() {
  const { isSuperAdmin, user } = useAuth();
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [activities, setActivities] = useState<AdminActivity[]>([]);
  
  // States for Add/Edit Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<AdminUser | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "", // Not actually stored in plain text normally, just for demo
    adminLevel: "RESTRICTED_ADMIN" as 'SUPER_ADMIN' | 'RESTRICTED_ADMIN',
    categories: [] as string[]
  });

  // State for Activity Modal
  const [selectedAdminForActivity, setSelectedAdminForActivity] = useState<AdminUser | null>(null);
  const [isActivityModalOpen, setIsActivityModalOpen] = useState(false);
  const [expandedMobileAdminId, setExpandedMobileAdminId] = useState<string | null>(null);

  useEffect(() => {
    setAdmins(getAdmins());
    setActivities(getActivities());
  }, []);

  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleOpenModal = (admin?: AdminUser) => {
    if (admin && admin.adminLevel === 'SUPER_ADMIN' && user?.id !== admin.id) {
      toast.error("Vous ne pouvez pas modifier un autre Super Administrateur.");
      return;
    }
    if (admin) {
      setEditingAdmin(admin);
      setFormData({
        name: admin.name,
        email: admin.email,
        password: "",
        adminLevel: admin.adminLevel,
        categories: admin.adminCategories || []
      });
    } else {
      setEditingAdmin(null);
      setFormData({
        name: "",
        email: "",
        password: "",
        adminLevel: "RESTRICTED_ADMIN",
        categories: []
      });
    }
    setIsModalOpen(true);
  };

  const handleSaveAdmin = () => {
    if (!formData.name || !formData.email) {
      toast.error("Veuillez remplir tous les champs obligatoires.");
      return;
    }

    if (editingAdmin) {
      updateAdmin(editingAdmin.id, {
        name: formData.name,
        email: formData.email,
        adminLevel: formData.adminLevel,
        adminCategories: formData.adminLevel === 'SUPER_ADMIN' ? [] : formData.categories
      });
      logAdminActivity(user!.id, user!.name, "Modification Admin", `A modifié les accès de ${formData.name}`);
      toast.success("Administrateur mis à jour avec succès.");
    } else {
      if (!formData.password) {
        toast.error("Un mot de passe est requis pour un nouvel administrateur.");
        return;
      }
      const newAdmin: AdminUser = {
        id: `admin-${Date.now()}`,
        name: formData.name,
        email: formData.email,
        adminLevel: formData.adminLevel,
        adminCategories: formData.adminLevel === 'SUPER_ADMIN' ? [] : formData.categories,
        createdAt: new Date().toISOString(),
        status: 'active'
      };
      storeAddAdmin(newAdmin);
      logAdminActivity(user!.id, user!.name, "Création Admin", `A créé le compte admin pour ${formData.name}`);
      toast.success("Administrateur créé avec succès.");
    }
    setAdmins(getAdmins());
    setActivities(getActivities());
    setIsModalOpen(false);
  };

  const handleToggleStatusAdmin = (admin: AdminUser) => {
    if (admin.adminLevel === 'SUPER_ADMIN' && user?.id !== admin.id) {
      toast.error("Action non autorisée sur un autre Super Administrateur.");
      return;
    }

    const newStatus = admin.status === 'active' ? 'suspended' : 'active';
    const actionText = newStatus === 'active' ? 'réactiver' : 'suspendre';
    
    if (confirm(`Êtes-vous sûr de vouloir ${actionText} l'accès de ${admin.name} ?`)) {
      updateAdmin(admin.id, { status: newStatus });
      logAdminActivity(user!.id, user!.name, newStatus === 'active' ? "Activation Admin" : "Suspension Admin", `A ${newStatus === 'active' ? 'réactivé' : 'suspendu'} le compte de ${admin.name}`);
      setAdmins(getAdmins());
      setActivities(getActivities());
      toast.success(`Administrateur ${newStatus === 'active' ? 'réactivé' : 'suspendu'}.`);
    }
  };

  const handlePermanentDeleteAdmin = (admin: AdminUser) => {
    if (admin.adminLevel === 'SUPER_ADMIN' && user?.id !== admin.id) {
      toast.error("Action non autorisée sur un autre Super Administrateur.");
      return;
    }

    if (confirm(`ATTENTION: Vous êtes sur le point de SUPPRIMER DÉFINITIVEMENT le compte de ${admin.name}. Cette action est irréversible. Confirmer ?`)) {
      removeAdmin(admin.id);
      logAdminActivity(user!.id, user!.name, "Suppression Admin", `A supprimé définitivement le compte de ${admin.name}`);
      setAdmins(getAdmins());
      setActivities(getActivities());
      toast.success(`Le compte de ${admin.name} a été supprimé définitivement.`);
    }
  };

  const handleSuspendAllRestrictedAdmins = () => {
    if (confirm("Êtes-vous sûr de vouloir suspendre TOUS les administrateurs restreints ? (Les Super Admins ne seront pas affectés)")) {
      const currentAdmins = getAdmins();
      const updatedAdmins = currentAdmins.map(a => 
        a.adminLevel === 'RESTRICTED_ADMIN' ? { ...a, status: 'suspended' as const } : a
      );
      saveAdmins(updatedAdmins);
      logAdminActivity(user!.id, user!.name, "Suspension Massive", "A suspendu tous les administrateurs restreints");
      setAdmins(updatedAdmins);
      setActivities(getActivities());
      toast.success("Tous les administrateurs restreints ont été suspendus.");
    }
  };

  const toggleCategory = (category: string) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category]
    }));
  };

  const exportActivityLog = (format: 'excel' | 'word' | 'pdf', specificAdminId?: string) => {
    toast.success(`Export ${format.toUpperCase()} en cours de génération...`);
    
    let title = "Bilan Global d'Activité des Administrateurs";
    let filename = "Bilan_Activite_Global";
    let dataToExport: any[] = [];

    if (specificAdminId) {
      const adminLogs = [...activities]
        .filter(a => a.adminId === specificAdminId)
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
        
      const admin = admins.find(a => a.id === specificAdminId);
      if (admin) {
        title = `Bilan d'Activité Détaillé - ${admin.name}`;
        filename = `Bilan_Activite_${admin.name.replace(/\s+/g, '_')}`;
      }

      let currentSession: { start: string, end: string, actions: string[], firstAction: string, lastAction: string } | null = null;
      const sessions = [];

      adminLogs.forEach(act => {
        if (act.action === "Connexion") {
          if (currentSession) sessions.push(currentSession);
          currentSession = { start: act.timestamp, end: "", actions: [], firstAction: "", lastAction: "" };
        } else if (act.action === "Déconnexion") {
          if (currentSession) {
            currentSession.end = act.timestamp;
            sessions.push(currentSession);
            currentSession = null;
          }
        } else {
          if (!currentSession) {
             currentSession = { start: act.timestamp, end: "", actions: [], firstAction: act.timestamp, lastAction: act.timestamp };
          }
          if (!currentSession.firstAction) currentSession.firstAction = act.timestamp;
          currentSession.lastAction = act.timestamp;
          currentSession.actions.push(`${new Date(act.timestamp).toLocaleTimeString('fr-FR', {hour: '2-digit', minute:'2-digit'})} - ${act.action}: ${act.details}`);
        }
      });
      if (currentSession) sessions.push(currentSession);

      const calculateDuration = (start: string, end: string) => {
        if (!start || !end) return "N/A";
        const diffMs = new Date(end).getTime() - new Date(start).getTime();
        const diffMins = Math.round(diffMs / 60000);
        if (diffMins < 60) return `${diffMins} min`;
        return `${Math.floor(diffMins / 60)}h ${diffMins % 60}m`;
      };

      dataToExport = sessions.map((s, index) => {
        const sessionEnd = s.end || s.lastAction || s.start;
        const connDuration = calculateDuration(s.start, sessionEnd);
        const actDuration = calculateDuration(s.firstAction || s.start, s.lastAction || s.firstAction || s.start);
        
        return {
          "Session": `Session ${index + 1}`,
          "Heure Connexion": new Date(s.start).toLocaleString('fr-FR'),
          "Heure Déconnexion": s.end ? new Date(s.end).toLocaleString('fr-FR') : (s.lastAction ? new Date(s.lastAction).toLocaleString('fr-FR') + ' (Est.)' : "N/A"),
          "Durée Connexion": connDuration,
          "Durée Activité": actDuration,
          "Actions Effectuées": s.actions.length > 0 ? s.actions.join("\n") : "Aucune"
        };
      });

      if (dataToExport.length === 0) {
        dataToExport = [{
          "Session": "Aucune session", "Heure Connexion": "-", "Heure Déconnexion": "-", "Durée Connexion": "-", "Durée Activité": "-", "Actions Effectuées": "-"
        }];
      }
    } else {
      dataToExport = activities.map(act => ({
        "Date & Heure": new Date(act.timestamp).toLocaleString('fr-FR'),
        "Administrateur": act.adminName,
        "Action": act.action,
        "Détails": act.details
      }));
    }

    generateExport(dataToExport, filename, title, format);
  };

  const openActivityModal = (admin: AdminUser) => {
    setSelectedAdminForActivity(admin);
    setIsActivityModalOpen(true);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-20">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">Gestion des Administrateurs</h1>
          <p className="text-muted-foreground font-body mt-1">
            Gérez les accès, les rôles et consultez l'activité de l'équipe d'administration.
          </p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2 font-bold bg-background">
                <Download className="w-4 h-4" /> Bilan Global (Tous)
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 z-10">
              <DropdownMenuItem onClick={() => exportActivityLog('excel')} className="cursor-pointer font-body">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" /> Excel (.csv)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportActivityLog('word')} className="cursor-pointer font-body">
                <FileText className="w-4 h-4 mr-2 text-blue-600" /> Word (.doc)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => exportActivityLog('pdf')} className="cursor-pointer font-body">
                <FileIcon className="w-4 h-4 mr-2 text-red-600" /> PDF (.pdf)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          <Button variant="outline" onClick={handleSuspendAllRestrictedAdmins} className="gap-2 font-bold bg-red-50 text-red-600 border-red-200 hover:bg-red-100 hover:text-red-700">
            <UserX className="w-4 h-4" /> Désactiver Tous
          </Button>
          
          <Button onClick={() => handleOpenModal()} className="gap-2 font-bold bg-primary hover:bg-primary/90">
            <UserPlus className="w-4 h-4" /> Ajouter Admin
          </Button>
        </div>
      </div>

      <Card className="border-border/50 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-muted-foreground uppercase bg-muted/40 font-semibold font-body border-b border-border">
              <tr>
                <th className="px-6 py-4 rounded-tl-lg">Administrateur</th>
                <th className="px-6 py-4">Niveau d'Accès</th>
                <th className="px-6 py-4">Catégories Autorisées</th>
                <th className="px-6 py-4">Statut</th>
                <th className="px-6 py-4 text-right rounded-tr-lg">Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((admin) => (
                <React.Fragment key={admin.id}>
                  <tr 
                    className={`border-b border-border/50 hover:bg-muted/10 transition-colors font-body cursor-pointer md:cursor-default ${admin.status === 'suspended' ? 'opacity-50' : ''}`}
                    onClick={() => {
                      if (window.innerWidth < 768) {
                        setExpandedMobileAdminId(expandedMobileAdminId === admin.id ? null : admin.id);
                      }
                    }}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                          admin.adminLevel === 'SUPER_ADMIN' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {admin.adminLevel === 'SUPER_ADMIN' ? <ShieldAlert className="w-5 h-5" /> : <ShieldCheck className="w-5 h-5" />}
                        </div>
                        <div>
                          <p className="font-bold text-foreground">{admin.name}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Mail className="w-3 h-3" /> {admin.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={admin.adminLevel === 'SUPER_ADMIN' ? "default" : "secondary"} className={
                        admin.adminLevel === 'SUPER_ADMIN' ? "bg-amber-500 hover:bg-amber-600" : ""
                      }>
                        {admin.adminLevel === 'SUPER_ADMIN' ? 'Super Admin' : 'Admin Restreint'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      {admin.adminLevel === 'SUPER_ADMIN' ? (
                        <span className="text-xs font-semibold text-muted-foreground italic">Accès Total</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {admin.adminCategories.slice(0, 2).map((cat, i) => (
                            <Badge key={i} variant="outline" className="text-[10px] bg-background">
                              {cat}
                            </Badge>
                          ))}
                          {admin.adminCategories.length > 2 && (
                            <Badge variant="outline" className="text-[10px] bg-background">+{admin.adminCategories.length - 2}</Badge>
                          )}
                          {admin.adminCategories.length === 0 && <span className="text-xs text-muted-foreground">Aucune</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="outline" className={admin.status === 'active' ? 'text-emerald-600 border-emerald-200 bg-emerald-50' : 'text-red-600 border-red-200 bg-red-50'}>
                        {admin.status === 'active' ? 'Actif' : 'Suspendu'}
                      </Badge>
                    </td>
                    <td className="hidden md:table-cell px-6 py-4 text-right space-x-2">
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openActivityModal(admin); }} title="Voir l'activité">
                        <Activity className="w-4 h-4 text-blue-600" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); handleOpenModal(admin); }} 
                        title="Modifier"
                        disabled={admin.adminLevel === 'SUPER_ADMIN' && user?.id !== admin.id}
                        className={`gap-2 ${admin.adminLevel === 'SUPER_ADMIN' && user?.id !== admin.id ? "opacity-30 cursor-not-allowed" : "hover:bg-emerald-50 text-emerald-600 border-emerald-200"}`}
                      >
                        <Edit className="w-3 h-3" /> Modifier
                      </Button>
                      {admin.id !== user?.id && (
                        <Button 
                          variant={admin.status === 'active' ? "outline" : "default"} 
                          size="sm" 
                          onClick={(e) => { e.stopPropagation(); handleToggleStatusAdmin(admin); }} 
                          disabled={admin.adminLevel === 'SUPER_ADMIN'}
                          className={`gap-2 ${admin.adminLevel === 'SUPER_ADMIN' ? "opacity-30 cursor-not-allowed" : ""} ${
                            admin.status === 'active' ? "text-amber-600 border-amber-200 hover:bg-amber-50" : "bg-emerald-600 hover:bg-emerald-700 text-white"
                          }`}
                        >
                          {admin.status === 'active' ? (
                            <><Pause className="w-3 h-3" /> Suspendre</>
                          ) : (
                            <><Power className="w-3 h-3" /> Activer</>
                          )}
                        </Button>
                      )}
                      {admin.id !== user?.id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => { e.stopPropagation(); handlePermanentDeleteAdmin(admin); }}
                          disabled={admin.adminLevel === 'SUPER_ADMIN'}
                          className={`gap-2 ${admin.adminLevel === 'SUPER_ADMIN' ? "opacity-30 cursor-not-allowed" : "text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"}`}
                          title="Supprimer définitivement"
                        >
                          <UserMinus className="w-3 h-3" /> Supprimer
                        </Button>
                      )}
                    </td>
                  </tr>
                  {expandedMobileAdminId === admin.id && (
                    <tr className="md:hidden bg-muted/10 border-b border-border/50">
                      <td colSpan={5} className="px-6 py-4">
                        <div className="flex flex-wrap gap-3 justify-end items-center">
                          <span className="text-xs text-muted-foreground mr-auto font-medium">Actions :</span>
                          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openActivityModal(admin); }} title="Voir l'activité">
                            <Activity className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={(e) => { e.stopPropagation(); handleOpenModal(admin); }} 
                            title="Modifier"
                            disabled={admin.adminLevel === 'SUPER_ADMIN' && user?.id !== admin.id}
                            className={`gap-2 ${admin.adminLevel === 'SUPER_ADMIN' && user?.id !== admin.id ? "opacity-30 cursor-not-allowed" : "hover:bg-emerald-50 text-emerald-600 border-emerald-200"}`}
                          >
                            <Edit className="w-3 h-3" /> Modifier
                          </Button>
                          {admin.id !== user?.id && (
                            <Button 
                              variant={admin.status === 'active' ? "outline" : "default"} 
                              size="sm" 
                              onClick={(e) => { e.stopPropagation(); handleToggleStatusAdmin(admin); }} 
                              disabled={admin.adminLevel === 'SUPER_ADMIN'}
                              className={`gap-2 ${admin.adminLevel === 'SUPER_ADMIN' ? "opacity-30 cursor-not-allowed" : ""} ${
                                admin.status === 'active' ? "text-amber-600 border-amber-200 hover:bg-amber-50" : "bg-emerald-600 hover:bg-emerald-700 text-white"
                              }`}
                            >
                              {admin.status === 'active' ? (
                                <><Pause className="w-3 h-3" /> Suspendre</>
                              ) : (
                                <><Power className="w-3 h-3" /> Activer</>
                              )}
                            </Button>
                          )}
                          {admin.id !== user?.id && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handlePermanentDeleteAdmin(admin); }}
                              disabled={admin.adminLevel === 'SUPER_ADMIN'}
                              className={`gap-2 ${admin.adminLevel === 'SUPER_ADMIN' ? "opacity-30 cursor-not-allowed" : "text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"}`}
                              title="Supprimer définitivement"
                            >
                              <UserMinus className="w-3 h-3" /> Supprimer
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl font-body">
          <DialogHeader>
            <DialogTitle className="text-xl font-display">{editingAdmin ? "Modifier l'Administrateur" : "Ajouter un Administrateur"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nom complet</Label>
                <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="Ex: Jean Dupont" />
              </div>
              <div className="space-y-2">
                <Label>Email (Identifiant de connexion)</Label>
                <Input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="admin@kaynin.com" />
              </div>
              {!editingAdmin && (
                <div className="space-y-2">
                  <Label>Mot de passe initial</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input type="password" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} className="pl-9" placeholder="••••••••" />
                  </div>
                  <p className="text-[10px] text-muted-foreground">Sera utilisé pour la première connexion.</p>
                </div>
              )}
              <div className="space-y-2 pt-2 border-t border-border">
                <Label>Niveau d'Administration</Label>
                <div className="flex gap-4 mt-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio" 
                      name="adminLevel" 
                      value="RESTRICTED_ADMIN"
                      checked={formData.adminLevel === "RESTRICTED_ADMIN"}
                      onChange={() => setFormData({...formData, adminLevel: "RESTRICTED_ADMIN"})}
                      className="accent-primary"
                    />
                    <span className="text-sm font-medium">Restreint (Choix des rubriques)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="radio" 
                      name="adminLevel" 
                      value="SUPER_ADMIN"
                      checked={formData.adminLevel === "SUPER_ADMIN"}
                      onChange={() => setFormData({...formData, adminLevel: "SUPER_ADMIN"})}
                      className="accent-primary"
                    />
                    <span className="text-sm font-medium text-amber-600">Super Admin (Accès Total)</span>
                  </label>
                </div>
              </div>
            </div>

            <div className={`space-y-3 ${formData.adminLevel === 'SUPER_ADMIN' ? 'opacity-50 pointer-events-none' : ''}`}>
              <Label>Catégories Accessibles</Label>
              <Card className="border border-border/50 shadow-none">
                <CardContent className="p-4 space-y-3 max-h-[250px] overflow-y-auto custom-scrollbar">
                  {ALL_CATEGORIES.map(category => (
                    <div key={category} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`cat-${category}`} 
                        checked={formData.categories.includes(category)}
                        onCheckedChange={() => toggleCategory(category)}
                      />
                      <label 
                        htmlFor={`cat-${category}`} 
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        {category}
                      </label>
                    </div>
                  ))}
                </CardContent>
              </Card>
              <p className="text-[10px] text-muted-foreground">
                Note: "Mon Compte" et "Vue d'ensemble" sont toujours accessibles par défaut.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsModalOpen(false)}>Annuler</Button>
            <Button onClick={handleSaveAdmin} className="bg-primary hover:bg-primary/90 text-primary-foreground">
              {editingAdmin ? "Enregistrer les modifications" : "Créer l'Administrateur"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity Log Modal */}
      <Dialog open={isActivityModalOpen} onOpenChange={setIsActivityModalOpen}>
        <DialogContent className="max-w-3xl font-body max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-display flex items-center justify-between">
              <span>Bilan d'activité : {selectedAdminForActivity?.name}</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2 h-8">
                    <Download className="w-3 h-3" /> Exporter
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40 z-10">
                  <DropdownMenuItem onClick={() => exportActivityLog('excel', selectedAdminForActivity?.id)} className="cursor-pointer text-xs">
                    <FileSpreadsheet className="w-3 h-3 mr-2 text-green-600" /> Excel (.csv)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportActivityLog('word', selectedAdminForActivity?.id)} className="cursor-pointer text-xs">
                    <FileText className="w-3 h-3 mr-2 text-blue-600" /> Word (.doc)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportActivityLog('pdf', selectedAdminForActivity?.id)} className="cursor-pointer text-xs">
                    <FileIcon className="w-3 h-3 mr-2 text-red-600" /> PDF (.pdf)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </DialogTitle>
            <DialogDescription>
              Historique des actions récentes effectuées sur la plateforme.
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto custom-scrollbar flex-1 -mx-6 px-6 py-2">
            <div className="space-y-4 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-border before:to-transparent">
              {activities.filter(a => a.adminId === selectedAdminForActivity?.id).length > 0 ? (
                activities.filter(a => a.adminId === selectedAdminForActivity?.id).map((act) => (
                  <div key={act.id} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-background bg-primary/20 text-primary shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 relative z-10">
                      <Activity className="w-4 h-4" />
                    </div>
                    <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border border-border/50 bg-card shadow-sm">
                      <div className="flex items-center justify-between space-x-2 mb-1">
                        <div className="font-bold text-foreground text-sm">{act.action}</div>
                        <time className="font-mono text-xs text-muted-foreground">{new Date(act.timestamp).toLocaleString('fr-FR', { hour: '2-digit', minute:'2-digit', day: '2-digit', month: 'short' })}</time>
                      </div>
                      <div className="text-sm text-muted-foreground">{act.details}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground italic">
                  Aucune activité récente trouvée.
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
